import { memo, useMemo } from "react";

import { useUserAvailableAssets, getZcCollateral, useRaydiumUpdatePositionTransaction } from "@bridgesplit/abf-react";
import { Column, TagTextAlert } from "@bridgesplit/ui";
import { bsMath, Result, LOADING_ERROR, decimalsToBps } from "@bridgesplit/utils";
import { AppButton } from "app/components/common";
import { useTransactionSender } from "app/components/transactions";
import { TxnTransferRaydiumPositionParams } from "@bridgesplit/abf-sdk";

import { getMaxBalanceBalanceWarning } from "../../util";
import { useRaydiumContext } from "../RaydiumContext";
import { useRaydiumOpenPositionBalances, useRaydiumOpenPositionLtv } from "./util";

export default memo(function OpenPositionCta() {
    const {
        openPositionForm,
        poolData,
        openPositionQuote,
        raydiumPosition,
        slippageController: { isAutoSlippage }
    } = useRaydiumContext();
    const { insufficientBalanceMessage } = useRaydiumOpenPositionBalances();
    const updateRaydiumPosition = useUpdateRaydiumPosition();
    const { insufficientCollateral } = useRaydiumOpenPositionLtv();
    const { data: userAssets } = useUserAvailableAssets({});

    const rawUserTokenAAmount = useMemo(() => {
        return userAssets?.find((asset) => asset.metadata.mint === raydiumPosition.tokenA.metadata.mint)?.amount ?? 0;
    }, [userAssets, raydiumPosition.tokenA.metadata.mint]);

    const rawUserTokenBAmount = useMemo(() => {
        return userAssets?.find((asset) => asset.metadata.mint === raydiumPosition.tokenB.metadata.mint)?.amount ?? 0;
    }, [userAssets, raydiumPosition.tokenB.metadata.mint]);

    const alertError = useMemo(() => {
        // don't show alert with no user input
        if (openPositionForm.status === undefined || !poolData) return undefined;
        if (insufficientBalanceMessage) return insufficientBalanceMessage;
        if (insufficientCollateral) return "Insufficient collateral for your loan debt";

        return undefined;
    }, [openPositionForm.status, poolData, insufficientBalanceMessage, insufficientCollateral]);

    const ctaError = useMemo(() => {
        if (!openPositionForm.tokenAAmount && !openPositionForm.tokenBAmount) return "Enter an amount";
        if (openPositionForm.tickLower === undefined || openPositionForm.tickUpper === undefined)
            return "Enter liquidity range";
        if (openPositionForm.status === undefined) return "No changes made";
        if (openPositionForm.status === "error") return "No quote found";
        if (!poolData || !openPositionQuote) return "Loading";
        return undefined;
    }, [
        openPositionForm.tokenAAmount,
        openPositionForm.tokenBAmount,
        openPositionForm.tickLower,
        openPositionForm.tickUpper,
        openPositionForm.status,
        poolData,
        openPositionQuote
    ]);

    const ctaWarning = useMemo(() => {
        return getMaxBalanceBalanceWarning(
            isAutoSlippage,
            raydiumPosition.tokenA.metadata,
            raydiumPosition.tokenB.metadata,
            bsMath.sub(openPositionForm.maxTokenAAmount, raydiumPosition.tokenA.amount),
            bsMath.sub(openPositionForm.maxTokenBAmount, raydiumPosition.tokenB.amount),
            rawUserTokenAAmount,
            rawUserTokenBAmount
        );
    }, [
        openPositionForm.maxTokenAAmount,
        openPositionForm.maxTokenBAmount,
        raydiumPosition.tokenA.metadata,
        raydiumPosition.tokenB.metadata,
        raydiumPosition.tokenA.amount,
        raydiumPosition.tokenB.amount,
        isAutoSlippage,
        rawUserTokenAAmount,
        rawUserTokenBAmount
    ]);

    return (
        <Column spacing={1}>
            {!!alertError && (
                <TagTextAlert color="error" icon="warning">
                    {alertError}
                </TagTextAlert>
            )}
            {!!ctaWarning && !alertError && (
                <TagTextAlert color="warning" icon="warning">
                    {ctaWarning}
                </TagTextAlert>
            )}

            <AppButton
                asyncCta={{ onClickWithResult: updateRaydiumPosition }}
                isTransaction
                color="secondary"
                disabled={!!ctaError || !!alertError}
            >
                {ctaError ?? "Modify position"}
            </AppButton>
        </Column>
    );
});

function useUpdateRaydiumPosition() {
    const {
        openPositionQuote,
        loanExpanded,
        reset,
        slippageController: { isAutoSlippage, slippagePercentDecimals }
    } = useRaydiumContext();
    const send = useTransactionSender();
    const updatePosition = useRaydiumUpdatePositionTransaction();
    return async () => {
        if (!openPositionQuote) return Result.errFromMessage(LOADING_ERROR);

        const positionMint = getZcCollateral(loanExpanded)?.assetMint;
        if (!positionMint) return Result.errFromMessage("No position mint found");

        const params: TxnTransferRaydiumPositionParams = {
            loan: loanExpanded.loan.address,
            positionMint,
            slippageDecimals: slippagePercentDecimals ?? 0,
            transferParams: {
                collateralIndex: 0,
                liquidityAmount: openPositionQuote.liquidity.toNumber(),
                transferParams: isAutoSlippage
                    ? { Liquidity: { slippageToleranceBps: decimalsToBps(0.1, "bps") } }
                    : {
                          TokenAmounts: {
                              tokenAAmount: openPositionQuote.maxTokenA.toNumber(),
                              tokenBAmount: openPositionQuote.maxTokenB.toNumber()
                          }
                      },
                tickLowerIndex: openPositionQuote.tickLowerIndex,
                tickUpperIndex: openPositionQuote.tickUpperIndex
            }
        };

        return await send(updatePosition, params, { sendOptions: { onSuccess: reset } });
    };
}
